/** Copy as draft — target date, optional gateway, static tag. */

import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { Copy, X } from 'lucide-react';

import { Button } from '@/components/ui';
import { resolveRouteId } from '@/features/smswiser-campaigns/components/SmswiserCampaignStatsTab';
import type { DropsWithStatsRow } from '@/features/smswiser-campaigns/types/workspace.types';
import type { Gateway } from '@/hooks';

import { CopyAsDraftGatewayAndTags } from './CopyAsDraftGatewayAndTags';
import { CopyAsDraftRowsPreview } from './CopyAsDraftRowsPreview';
import { CopyAsDraftTargetDateSection } from './CopyAsDraftTargetDateSection';
import { backdropClick } from '@/components/ui';

export interface CopyAsDraftModalProps {
  rows?: DropsWithStatsRow[];
  routes?: Gateway[];
  count?: number;
  defaultDate?: string;
  isBusy: boolean;
  onClose: () => void;
  onConfirm: (
    targetWorkDate: string,
    gatewayOverrides?: Record<string, number>,
    staticTagId?: string | null,
  ) => void;
}

export const CopyAsDraftModal: FC<CopyAsDraftModalProps> = ({
  rows = [],
  routes = [],
  count = 0,
  defaultDate,
  isBusy,
  onClose,
  onConfirm,
}) => {
  const [targetDate, setTargetDate] = useState(defaultDate ?? '');
  const [gatewayOverrides, setGatewayOverrides] = useState<Record<string, number>>({});
  const [staticTagId, setStaticTagId] = useState<string | null>(null);

  const effectiveCount = rows.length > 0 ? rows.length : count;
  const dropsNeedingGateway = useMemo(() => {
    if (rows.length === 0 || routes.length === 0) return [];
    return rows.filter((r) => resolveRouteId(r.drop.smsGatewayRoute, routes) === null);
  }, [rows, routes]);

  const gatewayOptions = useMemo(
    () =>
      routes.map((g) => ({
        value: g.id,
        label: `${g.name}${g.pricePerSms != null ? ` ($${Number(g.pricePerSms).toFixed(3)}/SMS)` : ''}`,
      })),
    [routes],
  );

  const singleGatewayValue = useMemo(() => {
    if (dropsNeedingGateway.length === 0) return -1;
    const ids = dropsNeedingGateway.map((r) => gatewayOverrides[r.drop.uniqueId ?? String(r.drop.id)]);
    if (ids.some((id) => id == null)) return -1;
    const first = ids[0];
    return ids.every((id) => id === first) ? first! : -1;
  }, [dropsNeedingGateway, gatewayOverrides]);

  const allGatewaysSelected =
    dropsNeedingGateway.length === 0 ||
    dropsNeedingGateway.every((r) => {
      const key = r.drop.uniqueId ?? String(r.drop.id);
      return gatewayOverrides[key] != null;
    });

  const handleBulkGatewayChange = (id: number) => {
    const next: Record<string, number> = {};
    for (const r of dropsNeedingGateway) {
      next[r.drop.uniqueId ?? String(r.drop.id)] = id;
    }
    setGatewayOverrides(next);
  };

  const handleConfirm = () => {
    if (!targetDate.trim()) return;
    if (dropsNeedingGateway.length > 0 && !allGatewaysSelected) return;
    onConfirm(
      targetDate.trim(),
      dropsNeedingGateway.length > 0 ? gatewayOverrides : undefined,
      staticTagId ?? undefined,
    );
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-background/90 p-4 backdrop-blur-sm"
      onClick={backdropClick(onClose)}
    >
      <div
        className="flex max-h-[90vh] w-full min-w-[280px] max-w-lg flex-col overflow-hidden rounded-xl border border-border bg-card shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-border bg-muted/20 px-4 py-3">
          <h3 className="flex items-center gap-2 text-base font-semibold">
            <Copy className="h-4 w-4 shrink-0" aria-hidden />
            Copy as draft
          </h3>
          <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={onClose} aria-label="Close">
            <X className="h-4 w-4" aria-hidden />
          </Button>
        </div>

        <div className="flex min-h-0 min-w-0 flex-col gap-4 overflow-y-auto p-4">
          {rows.length > 0 && <CopyAsDraftRowsPreview rows={rows} />}

          <p className="min-w-0 text-sm text-muted-foreground">
            Copy {effectiveCount} selected drop{effectiveCount !== 1 ? 's' : ''} as draft
            {effectiveCount !== 1 ? 's' : ''} to:
          </p>

          <CopyAsDraftTargetDateSection value={targetDate} onChange={setTargetDate} />

          <CopyAsDraftGatewayAndTags
            dropsNeedingGateway={dropsNeedingGateway}
            gatewayOptions={gatewayOptions}
            singleGatewayValue={singleGatewayValue}
            onBulkGatewayChange={handleBulkGatewayChange}
            staticTagId={staticTagId}
            onStaticTagChange={setStaticTagId}
          />

          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-border pt-4">
            <Button variant="outline" className="shrink-0" onClick={onClose} disabled={isBusy}>
              Cancel
            </Button>
            <Button
              className="shrink-0"
              disabled={isBusy || !targetDate.trim() || !allGatewaysSelected}
              onClick={handleConfirm}
            >
              Copy as draft
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CopyAsDraftModal;
